import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { sha256 } from "js-sha256";
import apiUrl from "../Apiurl";
import DataTable from "../interfaccia/DataTableBase";
import stilePulsanti from "../interfaccia/stilePulsanti";
import { BlockSkeleton } from "../interfaccia/loading";
import OrdineVoceCliccabile from "../logistica/AreaOrdiniComponenti/DBperAreaOrdiniComponenti/OrdineVoceCliccabile";
import ModificaOrdini from "../logistica/AreaOrdiniComponenti/ModificaOrdini";
import { optionLabel } from "../logistica/configurazione/adapters";
import { useLogisticaConfiguration } from "../logistica/configurazione/LogisticaConfigurationProvider";
import { getTradetrackerDateRange, parseTradetrackerCsv } from "./tradetracker/parseTradetrackerCsv";
import { composePrestashopClipboard, copyToClipboard, ESITI, selectTradetrackerRows } from "./tradetracker/tradetrackerSelection";

const API = `${apiUrl}/contab/controlli-tradetracker`;
const INITIAL_SELECTION = { esito: "TUTTI", ricerca: "" };
const FILTERS = ["TUTTI", "DA_VALUTARE", ...ESITI];
const esitoLabel = value => value === "TUTTI" ? "Tutti" : value === "DA_VALUTARE" ? "Da valutare" : value;
const dateLabel = value => value ? String(value).replace("T", " ").replace(/\.\d+Z$/, "") : "—";
const periodLabel = file => `${dateLabel(file.periodoDa).slice(0, 10)} / ${dateLabel(file.periodoA).slice(0, 10)}`;
const amountLabel = value => value == null ? "—" : Number(value).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const errorLabel = error => error.response?.data?.error || error.message || "Operazione non riuscita";

async function calcolaHashFile(buffer) {
  if (!window.crypto?.subtle) return sha256(new Uint8Array(buffer));
  const hash = await window.crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, "0")).join("");
}

const CLASSI_ESITO_SELEZIONATO = {
  APPROVA: stilePulsanti.buttonClassesGreen,
  RESO: stilePulsanti.buttonClassesOrange,
  ANNULLATO: stilePulsanti.buttonClassesRed,
};

function EsitoActions({ disabled, onChoose, suffix = "", current }) {
  return <div className="flex flex-wrap items-center">
    {[...ESITI, null].map(esito => {
      const selezionato = esito !== null && esito === current;
      return <button key={esito || "reset"} type="button" aria-pressed={esito ? selezionato : undefined}
        className={(selezionato ? CLASSI_ESITO_SELEZIONATO[esito] : stilePulsanti.buttonClassesWhite) + " mv1"}
        disabled={disabled} onClick={() => onChoose(esito)}>{esito || "Annulla decisione"}{suffix}</button>;
    })}
  </div>;
}

function alertLabel(alert) {
  if (alert.codice === "IMPORTO_DIVERSO") {
    if (alert.importoTradetrackerIvato != null) {
      return `Importo TT ${amountLabel(alert.importoTradetrackerIvato)} (imponibile ${amountLabel(alert.importoTradetracker)} + IVA ${alert.aliquotaIva}%) diverso dalle spedizioni (${amountLabel(alert.importoSpedizioni)})`;
    }
    return `Importo TT ${amountLabel(alert.importoTradetracker)} diverso dalle spedizioni (${amountLabel(alert.importoSpedizioni)})`;
  }
  return alert.codice;
}

function TableSkeleton() {
  return <div className="w-100 pv3" role="status" aria-label="Caricamento controlli TradeTracker">
    <BlockSkeleton height={240} />
  </div>;
}

export default function ControlliTradetracker({ token, utente, temaColori, mobile }) {
  const { getAccessTokenSilently } = useAuth0();
  const { datiTendineOrdine, canRead: canReadConfiguration } = useLogisticaConfiguration();
  const canRead = Boolean(utente?.permessiAPI?.["read:contabile"] || utente?.permessiAPI?.["read:logistica"]);
  const canWrite = Boolean(utente?.permessiAPI?.["update:contabile"]);
  const [searchParams, setSearchParams] = useSearchParams();
  const fileId = searchParams.get("fileId");
  const [files, setFiles] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readError, setReadError] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [selection, setSelection] = useState(INITIAL_SELECTION);
  const [selectedIds, setSelectedIds] = useState([]);
  const [clearSelection, setClearSelection] = useState(false);
  const [busy, setBusy] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [nome, setNome] = useState("");
  const [fonte, setFonte] = useState("");
  const [ordine, setOrdine] = useState("NO DATA");
  const mounted = useRef(false);
  const readRequest = useRef(null);
  const parseVersion = useRef(0);
  const writing = useRef(false);
  const currentFileId = useRef(fileId);
  currentFileId.current = fileId;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; readRequest.current?.abort(); parseVersion.current += 1; };
  }, []);

  useEffect(() => {
    readRequest.current?.abort();
    if (!canRead) return undefined;
    const controller = new AbortController();
    readRequest.current = controller;
    const isCurrent = () => mounted.current && !controller.signal.aborted &&
      readRequest.current === controller && currentFileId.current === fileId;
    setLoading(true);
    setReadError("");
    (async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        if (!isCurrent()) return;
        const response = await axios.get(`${API}/files${fileId ? `/${encodeURIComponent(fileId)}` : ""}`, {
          signal: controller.signal, headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!isCurrent()) return;
        if (fileId) setDetail(response.data);
        else setFiles(response.data.data || []);
      } catch (err) {
        if (isCurrent() && !axios.isCancel(err)) setReadError(errorLabel(err));
      } finally {
        if (isCurrent()) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [canRead, fileId, getAccessTokenSilently, refreshVersion]);

  useEffect(() => {
    setSelection(INITIAL_SELECTION);
    setError(""); setMessage(""); setOrdine("NO DATA");
  }, [fileId]);

  useEffect(() => {
    setSelectedIds([]);
    setClearSelection(previous => !previous);
    setMessage("");
  }, [selection, fileId]);

  const activeDetail = detail && String(detail.file.id) === fileId ? detail : null;
  const rows = activeDetail?.righe;
  const visibleRows = useMemo(() => selectTradetrackerRows(rows || [], selection), [rows, selection]);
  const selectedVisibleIds = useMemo(() => visibleRows.filter(row => selectedIds.includes(row.id)).map(row => row.id), [visibleRows, selectedIds]);
  const handleSelectedRowsChange = useCallback(state => setSelectedIds(state.selectedRows.map(row => row.id)), []);

  const aggiornaEsito = useCallback(async (idRighe, esito) => {
    if (writing.current || !idRighe.length || !fileId || !canWrite) return;
    if (!utente?.username?.trim()) { setError("Username Domino mancante: impossibile salvare l'esito. Ricaricare il profilo utente."); return; }
    writing.current = true; setBusy(true); setError(""); setMessage("");
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.patch(`${API}/righe/esito`, {
        idFile: Number(fileId), idRighe, esito, username: utente.username,
      }, { headers: { Authorization: `Bearer ${accessToken}` } });
      if (!mounted.current) return;
      const ids = new Set(idRighe);
      setFiles(previous => previous.map(file => String(file.id) === fileId ? { ...file, conteggi: response.data.conteggi } : file));
      if (currentFileId.current !== fileId) return;
      setDetail(previous => previous && String(previous.file.id) === fileId ? {
        ...previous,
        file: { ...previous.file, conteggi: response.data.conteggi },
        righe: previous.righe.map(row => ids.has(row.id) ? { ...row, esito, esitoAutomatico: false } : row),
      } : previous);
      setSelectedIds([]); setClearSelection(previous => !previous);
      setMessage(`Aggiornate ${response.data.aggiornate} righe`);
    } catch (err) {
      if (mounted.current && currentFileId.current === fileId) setError(errorLabel(err));
    } finally {
      writing.current = false;
      if (mounted.current) setBusy(false);
    }
  }, [canWrite, fileId, getAccessTokenSilently, utente?.username]);

  async function scegliFile(event) {
    const file = event.target.files?.[0];
    const version = ++parseVersion.current;
    setPreview(null); setNome(file?.name || ""); setError(""); setMessage("");
    setParsing(Boolean(file));
    if (!file) return;
    try {
      const buffer = await file.arrayBuffer();
      const parsedRows = parseTradetrackerCsv(buffer);
      const hashFile = await calcolaHashFile(buffer);
      if (mounted.current && version === parseVersion.current) setPreview({
        nomeFileOriginale: file.name, rows: parsedRows, hashFile, ...getTradetrackerDateRange(parsedRows),
      });
    } catch (err) {
      if (mounted.current && version === parseVersion.current) setError(errorLabel(err));
    } finally {
      if (mounted.current && version === parseVersion.current) setParsing(false);
    }
  }

  async function importa(event) {
    event.preventDefault();
    if (writing.current || !preview || !canWrite) return;
    if (!utente?.username?.trim()) { setError("Username Domino mancante: impossibile importare. Ricaricare il profilo utente."); return; }
    if (!nome.trim() || nome.trim().length > 150 || !["VIG", "BOT"].includes(fonte)) {
      setError("Inserire un nome (massimo 150 caratteri) e scegliere la fonte VIG o BOT."); return;
    }
    writing.current = true; setBusy(true); setError(""); setMessage("");
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.post(`${API}/files`, {
        nome: nome.trim(), nomeFileOriginale: preview.nomeFileOriginale, fonte,
        hashFile: preview.hashFile, rows: preview.rows, username: utente.username,
      }, { headers: { Authorization: `Bearer ${accessToken}` } });
      if (!mounted.current) return;
      setPreview(null); setNome("");
      setSearchParams({ fileId: String(response.data.id) });
    } catch (err) {
      if (mounted.current) setError(errorLabel(err));
    } finally {
      writing.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  async function copia() {
    const snapshot = composePrestashopClipboard(rows || [], selection);
    setError(""); setMessage("");
    try {
      await copyToClipboard(snapshot.text);
      if (mounted.current && currentFileId.current === fileId) setMessage(`Copiati ${snapshot.count} id`);
    } catch (err) {
      if (mounted.current && currentFileId.current === fileId) setError(errorLabel(err));
    }
  }

  const fileColumns = [
    { name: "Nome", selector: row => row.nome, sortable: true, grow: 2,
      cell: row => <button type="button" className={stilePulsanti.buttonClassesWhite} onClick={() => setSearchParams({ fileId: String(row.id) })}>{row.nome}</button> },
    { name: "Fonte", selector: row => row.fonte, sortable: true, compact: true },
    { name: "Range date", selector: periodLabel, minWidth: "200px" },
    { name: "Importazione", selector: row => dateLabel(row.createdAt), minWidth: "180px", sortable: true },
    { name: "Utente", selector: row => row.createdBy, minWidth: "200px" },
    { name: "Righe", selector: row => row.righeTotali, sortable: true },
    ...FILTERS.slice(1).map(esito => ({ name: esitoLabel(esito), selector: row => row.conteggi?.[esito] || 0 })),
  ];
  const rowColumns = [
    { name: <span title="Riga CSV">Riga</span>, selector: row => row.numeroRiga, width: "40px", compact: true },
    { name: <span title="ID transazione TT">ID TT</span>, selector: row => row.idTransazioneTt, sortable: true, width: "104px", compact: true, wrap: true },
    { name: <span title="ID ordine Prestashop">ID PS</span>, selector: row => row.idOrdinePs, sortable: true, width: "70px", compact: true, wrap: true },
    { name: "Stato TT", selector: row => row.statoTt, width: "80px", compact: true, wrap: true },
    { name: <span className="ws-normal">Sito affiliato</span>, selector: row => row.sitoAffiliato, width: "90px", compact: true, wrap: true, style: { overflowWrap: "anywhere" } },
    { name: <span className="ws-normal" title="Importo TradeTracker imponibile, IVA esclusa">Imponibile TT</span>, selector: row => Number(row.importoOrdine), cell: row => amountLabel(row.importoOrdine), sortable: true, width: "100px", compact: true, right: true },
    { name: <span title="Commissione">Comm.</span>, selector: row => Number(row.commissione), cell: row => amountLabel(row.commissione), sortable: true, width: "96px", compact: true, right: true },
    { name: "Date TT", width: "120px", compact: true, cell: row => <div className="pv2 ph1">
      {[["Reg.", "Registrazione", row.dataRegistrazione], ["Val.", "Validazione", row.dataValidazione], ["Clic", "Clic", row.dataClick]].map(([label, title, value]) =>
        <div key={label} className="mb1"><abbr title={title}>{label}</abbr>: {dateLabel(value).slice(0, 10)}
          {value && <div>{dateLabel(value).slice(11)}</div>}
        </div>)}
    </div> },
    { name: "Spedizioni Domino", width: "210px", cell: row => row.spedizioni?.length ? <div className="pv2">
      {row.spedizioni.map(spedizione => <div key={spedizione.idSpedizione} className="mb2">
        <OrdineVoceCliccabile setOrdineFunction={setOrdine} idSped={spedizione.idSpedizione}
          datoDisplay={<span className="b underline">#{spedizione.idSpedizione}</span>} />
        <div>{optionLabel(datiTendineOrdine?.statoOrdine, spedizione.statoOrdine)}</div>
        <div>Data: {dateLabel(spedizione.data)}</div>
        <div>Partenza: {dateLabel(spedizione.dataPartenza)}</div>
        <div>{amountLabel(spedizione.totaleImportoSpedizione)} · {spedizione.tipo} · {spedizione.fonte}</div>
      </div>)}
    </div> : <span className="tastoArancio br2 pa2 b">Nessuna spedizione</span> },
    { name: "Alert", width: "220px", cell: row => row.alert?.length ? <div className="pv2">
      {row.alert.map(alert => <div key={alert.codice} className="tastoArancio white br2 pa2 mb1">{alertLabel(alert)}</div>)}
    </div> : null },
    { name: "Esito", width: canWrite ? "170px" : "140px", compact: true, cell: row => <div className="pv2 ph1 w-100">
      <strong>{row.esito || "Da valutare"}</strong>
      {row.esito && row.esitoAutomatico && <span className="ml2 i">(pre-valorizzato dallo stato spedizioni)</span>}
      {canWrite && <EsitoActions current={row.esito} disabled={busy || loading} onChoose={esito => aggiornaEsito([row.id], esito)} />}
    </div> },
  ];

  if (!canRead) return <div className="pa3" role="alert">Per consultare i controlli serve read:contabile oppure read:logistica.</div>;
  if (ordine !== "NO DATA") return <ModificaOrdini utente={utente} token={token} ordine={ordine}
    setOrdineFunction={setOrdine} datiTendineOrdine={datiTendineOrdine} temaColori={temaColori} mobile={mobile} />;

  return <div className="sfondoBiancoTema pa3">
    <div className="flex flex-wrap items-center justify-between bb bordoColAlt pb3 mb3">
      <h1 className="f4 ma0">Controlli Tradetracker</h1>
      <div className="flex flex-wrap">
        {fileId && <button type="button" className={stilePulsanti.buttonClassesWhite} disabled={busy}
          onClick={() => setSearchParams({})}>Elenco file</button>}
        <button type="button" className={stilePulsanti.buttonClassesWhite} disabled={busy || loading}
          onClick={() => setRefreshVersion(previous => previous + 1)}>Aggiorna</button>
      </div>
    </div>
    <p>I controlli più vecchi di 6 mesi vengono eliminati automaticamente</p>
    {error && <div role="alert" className="tastoRosso white pa2 br2 mb3">{error}</div>}
    {message && <p role="status">{message}</p>}
    {readError && <div role="alert" className="tastoRosso white pa2 br2 mb3">{readError}</div>}

    {!fileId && <>
      {canWrite ? <form onSubmit={importa} className="ba bordoColAlt br3 pa3 mb3">
        <h2 className="f5 mt0">Importa file TradeTracker</h2>
        <label className="db mb2">File CSV
          <input type="file" accept=".csv" onChange={scegliFile} disabled={busy} className="db mt1 mw-100" required />
        </label>
        <label className="db">Nome
          <input className={stilePulsanti.inputClasses} value={nome} onChange={event => setNome(event.target.value)} maxLength={150} required disabled={busy} />
        </label>
        <label className="db">Fonte Prestashop
          <select className={stilePulsanti.inputClasses} value={fonte} onChange={event => setFonte(event.target.value)} required disabled={busy}>
            <option value="">Scegli la fonte</option><option value="VIG">VIG</option><option value="BOT">BOT</option>
          </select>
        </label>
        {parsing && <div role="status" aria-label="Lettura CSV"><BlockSkeleton height={60} /></div>}
        {preview && <p>Anteprima: {preview.rows.length} righe · Range date: {periodLabel(preview)}</p>}
        <button className={stilePulsanti.buttonClassesGreen} type="submit" disabled={busy || parsing || !preview}>Importa</button>
        {busy && <div role="status" aria-label="Salvataggio file"><BlockSkeleton height={40} /></div>}
      </form> : <p>L’importazione e la modifica degli esiti richiedono update:contabile.</p>}
      <h2 className="f5">File importati</h2>
      <DataTable columns={fileColumns} data={files} keyField="id" progressPending={loading} progressComponent={<TableSkeleton />}
        noDataComponent="Nessun file importato" pagination paginationPerPage={20} highlightOnHover />
    </>}

    {fileId && (loading ? <TableSkeleton /> : !readError && activeDetail && <>
      <h2 className="f5">{activeDetail.file.nome} · {activeDetail.file.fonte}</h2>
      <p>{activeDetail.file.nomeFileOriginale} · {periodLabel(activeDetail.file)} · Importato il {dateLabel(activeDetail.file.createdAt)} da {activeDetail.file.createdBy}</p>
      {canReadConfiguration && !datiTendineOrdine && <p>Etichette logistica non disponibili: sono mostrati i valori dello stato ordine.</p>}
      <div className="flex flex-wrap items-end mb3">
        <label className="mr3">Esito
          <select className={stilePulsanti.inputClasses} value={selection.esito} disabled={busy}
            onChange={event => {
              const esito = event.target.value;
              setSelection(previous => ({ ...previous, esito }));
            }}>
            {FILTERS.map(esito => <option key={esito} value={esito}>{esitoLabel(esito)} ({esito === "TUTTI" ? activeDetail.file.righeTotali : activeDetail.file.conteggi?.[esito] || 0})</option>)}
          </select>
        </label>
        <label className="mr3">Cerca id ordine / transazione
          <input className={stilePulsanti.inputClasses} value={selection.ricerca} disabled={busy}
            onChange={event => {
              const ricerca = event.target.value;
              setSelection(previous => ({ ...previous, ricerca }));
            }} />
        </label>
        <button type="button" className={stilePulsanti.buttonClassesWhite + " mb2"} onClick={copia}
          disabled={busy || !ESITI.includes(selection.esito) || !visibleRows.length}>Copia id Prestashop</button>
      </div>
      <p>{visibleRows.length} righe filtrate su {activeDetail.file.righeTotali}. La copia include tutti gli id del filtro e della ricerca, anche nelle altre pagine, senza duplicati.</p>
      {canWrite && <div className="ba bordoColAlt br3 pa2 mb3">
        <div>Righe selezionate: {selectedVisibleIds.length}</div>
        <EsitoActions suffix=" selezionate" disabled={busy || !selectedVisibleIds.length} onChoose={esito => aggiornaEsito(selectedVisibleIds, esito)} />
      </div>}
      {busy && <div role="status" aria-label="Salvataggio esiti"><BlockSkeleton height={40} /></div>}
      <DataTable key={`${fileId}-${selection.esito}-${selection.ricerca}`} columns={rowColumns} data={visibleRows} keyField="id"
        noDataComponent="Nessuna riga per i filtri selezionati" pagination paginationPerPage={20} paginationRowsPerPageOptions={[10, 20, 100, 200]}
        dense highlightOnHover selectableRows={canWrite} selectableRowsVisibleOnly selectableRowDisabled={() => busy}
        onSelectedRowsChange={handleSelectedRowsChange} clearSelectedRows={clearSelection} />
    </>)}
  </div>;
}
